import React, { FC, useState, useRef } from "react";
import Seo from "components/Seo/Seo";
import { trackEvent } from "utils/analytics";
import { useForm } from "hooks";
import SocialsList from "shared/SocialsList/SocialsList";
import { MapPin, Mail, Phone, Send, Loader2 } from "lucide-react";
import { roomApi } from "../../api";

export interface PageContactProps {
  className?: string;
}

const info = [
  {
    Icon: MapPin,
    title: "Dirección",
    desc: "Av. del Carrilet, 237, 08907 L’Hospitalet de Llobregat, Barcelona",
  },
  {
    Icon: Mail,
    title: "Email",
    desc: "info@presenta.biz",
  },
  {
    Icon: Phone,
    title: "Llámanos",
    desc: "+(34) 675 92 38 26",
  },
];

const contactFormFields = {
  name: "",
  email: "",
  phone: "",
  message: "",
};

const inputDark =
  "w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-slate-500 transition focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/30";
const labelDark = "mb-1.5 block text-sm font-medium text-slate-300";

const PageContact: FC<PageContactProps> = ({ className = "" }) => {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");
  const { name, email, phone, message, onInputChange, onResetForm } = useForm(contactFormFields);
  // Anti-spam: honeypot (campo señuelo oculto) + marca de tiempo de apertura del formulario.
  const [website, setWebsite] = useState("");
  const formLoadedAt = useRef(Date.now());

  const handleContact = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFeedback("");

    if (!name.trim() || !email.trim() || !message.trim()) {
      setStatus("error");
      setFeedback("Completa al menos nombre, email y mensaje.");
      return;
    }

    try {
      setStatus("loading");
      const { data } = await roomApi.post("/contact", { name, email, phone, message, website, elapsedMs: Date.now() - formLoadedAt.current });
      if (data?.ok) {
        setStatus("success");
        setFeedback(data.msg || "Mensaje enviado correctamente. Te responderemos pronto.");
        trackEvent("generate_lead", { form: "contact" }); // GA4: contacto enviado
        onResetForm();
      } else {
        setStatus("error");
        setFeedback(data?.msg || "No se pudo enviar el mensaje.");
      }
    } catch (err: any) {
      const resp = err?.response?.data;
      // express-validator devuelve errors como objeto { campo: { msg } } o como array
      const firstError = resp?.errors
        ? (Array.isArray(resp.errors) ? resp.errors[0]?.msg : (Object.values(resp.errors)[0] as any)?.msg)
        : undefined;
      setStatus("error");
      setFeedback(resp?.msg || firstError || "No se pudo enviar el mensaje. Inténtalo de nuevo.");
    }
  };

  return (
    <div className={`nc-PageContact relative min-h-screen overflow-hidden bg-black px-4 py-14 ${className}`} data-nc-id="PageContact">
      <Seo
        path="/contact"
        title="Contacto | Karaoke Rooms Barcelona"
        description="¿Dudas o reservas de grupo? Contacta con Karaoke Rooms Barcelona, salas privadas de karaoke en el centro de Barcelona. Te respondemos enseguida."
        keywords="contacto karaoke barcelona, reservas grupo karaoke barcelona"
      />

      <div className="relative mx-auto max-w-5xl">
        {/* Cabecera */}
        <div className="flex flex-col items-center text-center">
          <h1 className="text-3xl font-bold uppercase tracking-wide text-white sm:text-4xl">Contacto</h1>
          <div className="mt-3 h-1 w-20 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500" />
          <p className="mt-4 max-w-md text-sm text-slate-400">¿Tienes una pregunta o quieres reservar? Escríbenos y te respondemos lo antes posible.</p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-5">
          {/* Información */}
          <div className="space-y-4 lg:col-span-2">
            {info.map((item) => (
              <div key={item.title} className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-5 shadow-xl backdrop-blur-xl">
                <span className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-xl bg-violet-500/15 text-violet-300">
                  <item.Icon size={20} />
                </span>
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">{item.title}</h3>
                  <span className="mt-1 block text-white">{item.desc}</span>
                </div>
              </div>
            ))}
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 shadow-xl backdrop-blur-xl">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Síguenos</h3>
              <SocialsList className="mt-3" />
            </div>
          </div>

          {/* Formulario */}
          <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 shadow-2xl backdrop-blur-xl sm:p-8 lg:col-span-3">
            <form className="grid grid-cols-1 gap-5" onSubmit={handleContact} method="post">
              {/* Honeypot anti-bot: invisible para humanos. Si llega con valor, el backend
                  descarta el mensaje. */}
              <input
                type="text"
                name="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
              />
              <div>
                <label htmlFor="name" className={labelDark}>Nombre</label>
                <input id="name" type="text" placeholder="Tu nombre" name="name" value={name} onChange={onInputChange} className={inputDark} />
              </div>
              <div>
                <label htmlFor="phone" className={labelDark}>Teléfono</label>
                <input id="phone" type="tel" placeholder="0034 000 000 000" name="phone" value={phone} onChange={onInputChange} className={inputDark} />
              </div>
              <div>
                <label htmlFor="email" className={labelDark}>Email</label>
                <input id="email" type="email" placeholder="tuemail@karaokeroomsbarcelona.com" name="email" value={email} onChange={onInputChange} className={inputDark} />
              </div>
              <div>
                <label htmlFor="message" className={labelDark}>Mensaje</label>
                <textarea id="message" rows={6} placeholder="Escribe tu mensaje…" name="message" value={message} onChange={onInputChange} className={`${inputDark} resize-none`} />
              </div>
              <button
                type="submit"
                disabled={status === "loading"}
                className="group inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-600 px-7 py-3 text-base font-bold uppercase tracking-wide text-white shadow-lg shadow-violet-600/30 transition-all duration-200 hover:from-violet-500 hover:to-fuchsia-500 hover:shadow-violet-500/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 active:scale-95 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {status === "loading" ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Enviando…
                  </>
                ) : (
                  <>
                    <Send size={18} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                    Enviar consulta
                  </>
                )}
              </button>

              {feedback && (
                <p
                  role="status"
                  aria-live="polite"
                  className={`rounded-lg border px-4 py-3 text-sm ${
                    status === "success"
                      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                      : "border-rose-500/30 bg-rose-500/10 text-rose-300"
                  }`}
                >
                  {feedback}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageContact;
